// @flow

import React, { useEffect, useRef } from 'react';
import { Switch, Route, useHistory, useLocation, Router } from 'react-router-dom';
// import { createBrowserHistory } from 'history';
import i18n from 'i18next';
import queryString from 'query-string';
import resources from '../sp-resources.json';
import * as R from 'ramda';
import { isMobile } from 'react-device-detect';

import PrivateRoute from './PrivateRoute';

import Inspectlet from '../services/inspectlet';
import GA from '../services/gAnalytics';
import adTracking from '../services/adTracking';
import { onAuthStateChanged, auth } from '../services/firebase';
import SPBackend from '../services/SPBackend';
import { registerLogoutHandler } from '../services/authUtils';

// import HeaderPublic from "./HeaderPublic";
import { useSelector, useDispatch } from 'react-redux';
import {
  logout,
  fetchUserProfile
} from '../reduxStore/modules/user/actions';
import { setAuth, setAuthLoading, clearAuth } from '../reduxStore/modules/auth/actions';
import { setI18n, setIsAppLoading, setLoadingModule, setSnackMessage } from '../reduxStore/modules/common/actions';
import store from '../reduxStore';
import { redirectBasedOnAccountStatus } from '../utils/pathUtils';
import { log } from '../utils/jsUtils';
import { feedApi, tagsApi } from '../features/api';
import RoutesContainer from './RoutesContainer';
import MemberHome from './Feed/MemberHome';
import RequestHome from './Feed/RequestHome';
import PropertyHome from './Properties/PropertyHome';
import PropertiesHome from './Properties/PropertiesHome';
import StaffListHome from './StaffMgt/StaffListHome';
import TenantsHome from './Tenants/TenantsHome';
import AnalyticsHome from './Analytics/AnalyticsHome';
import LandingPage from './v2/pages/LandingPage';
import PlansHome from './Billing/PlansHome';
import CheckoutHome from './Billing/CheckoutHome';
import SubscribeHome from './Billing/SubscribeHome';
import ActivateHome from './Billing/ActivateHome';
import BillingHome from './Billing/BillingHome';
import BotSettingsHome from './BotSettings/BotSettingsHome';
import StaffInvitationHome from './PublicCpts/StaffInvitationHome';
import TwilioNumberHome from './TwilioNumber/TwilioNumberHome';
import Login from './PublicCpts/Login';
import Signup from './PublicCpts/Signup';
import SignupPage from './v2/pages/SignupPage';
import VerifyPhoneHome from './VerifyPhone/VerifyPhoneHome';
import TagsHome from './Tags/TagsHome';
import IntegrationsHome from './Integrations/IntegrationsHome';
import ReferralsHome from './Referrals/ReferralsHome';
import AuthorizeHome from './Auth/AuthorizeHome';
import Logout from './Logout';
import ApiDocs from './ApiDocs/index';
import InvalidUrl from './InvalidUrl';
import pathUtils from '../utils/pathUtils';
import { setPlanId } from '../reduxStore/modules/billing/actions';

GA.initialize();
Inspectlet.initialize();
adTracking.captureClickIds();

type Props = {
  location: Object
};

const SquarePlanApp = (props: Props) => { 
  const dispatch = useDispatch(); 
  const location = useLocation();
  const history = useHistory();
  log('[SquarePlanApp] history: ', history);

  const i18nInitialized = useRef(false);
  const parsed = queryString.parse(location.search);
  // log('[SquarePlanApp] render parsed: ', parsed);
  const { planid: paramPlanId, ppId: pushPlayerId } = parsed;
  // TODO: store in backend this user's push player id
  // console.log('pushPlayerId: ', pushPlayerId);

  // Use a more reliable method to determine if logged in
  const initialLoggedInState = () => {
    return !!auth.currentUser;
  };
  
  const initialState = {
    loggedIn: initialLoggedInState(),
    redirectToReferrer: false,
    email: '',
    token: ''
  };
  log('[SquarePlanApp] initialState: ', initialState);
  
  const user = useSelector(state => state.user);
  // Use auth slice for login status (uid is now in auth, not user)
  const loggedIn = useSelector(state => state.auth && !!state.auth.uid);
  const billing = useSelector(state => state.billing);

  // Debug effect to monitor auth state
  // useEffect(() => {
  //   const logAuthState = () => {
  //     const localStorageLoggedIn = window.localStorage.getItem('loggedIn') === 'true';
  //     const fbUser = auth.currentUser;
      
  //     log('[SquarePlanApp] Auth state check:', {
  //       localStorageLoggedIn,
  //       hasFirebaseUser: !!fbUser,
  //       reduxUserUid: user?.uid || null,
  //       initialLoggedIn: loggedIn
  //     });
  //   };
    
  //   logAuthState();
    
  //   // Check auth state every 2 seconds for debugging
  //   const interval = setInterval(logAuthState, 2000);
    
  //   return () => clearInterval(interval);
  // }, [user, loggedIn]);

  const dispatchLogout = () => {
    log('dispatchLogout pathname: ', history.location.pathname);
    const access = R.find(R.propEq('pathname', history.location.pathname))(pathUtils.pathAuth);
    log('dispatchLogout access: ', access);
    
    if (typeof access !== 'undefined'
      && access !== null
      && access.restricted) {
      window.localStorage.removeItem('loggedIn');
      dispatch(logout());
      dispatch(setSnackMessage({
        message: 'Your secure session has expired. Please login again.',
        level: 'warning',
        duration: 5000
      }));
      history.push('/');
    }
  };
  
  // Firebase auth state listener
  // - Login case: login() action handles profile fetch, this just syncs setAuth
  // - Logout case: handle session expiry and cleanup
  // - Page refresh: fetch profile if accountId is empty (indicates fresh load)
  useEffect(() => {
    log('[App componentDidMount] loggedIn: ', loggedIn ? 'true' : 'false');

    // Set auth state to loading while we wait for Firebase
    dispatch(setAuthLoading(true));

    const unsubscribe = onAuthStateChanged(auth, fbUser => {
      if (!fbUser) {
        // Logout or session expiry
        log('[SquarePlanApp] No Firebase user detected');
        dispatch(clearAuth());
        dispatchLogout();
        dispatch(setAuthLoading(false));
      } else {
        // User exists - could be:
        // 1. Login (handled by login() action - profile already fetched)
        // 2. Page refresh (need to fetch profile)
        log('[SquarePlanApp] Firebase user detected:', fbUser.email);

        // Check if profile already loaded BEFORE updating auth state
        // This check must happen before setAuth because setAuth doesn't touch user state,
        // but we want to know if login() already populated the profile
        const currentAccountId = store.getState().user.accountId;
        const profileAlreadyLoaded = !!currentAccountId;

        // Sync Firebase auth state to Redux (only updates auth slice, NOT user profile)
        dispatch(setAuth(fbUser));
        dispatch(setAuthLoading(false));

        if (profileAlreadyLoaded) {
          log('[SquarePlanApp] Profile already loaded (login case), skipping fetch');
          return;
        }

        // Page refresh case - need to fetch profile
        log('[SquarePlanApp] Page refresh detected - fetching profile');

        // Note: RTK Query caches are NOT reset on page refresh - cache is preserved
        // and RTK Query will naturally refetch based on its configuration.
        // Cache reset only happens on login/logout (user switch) in the respective actions.

        dispatch(setIsAppLoading(true));

        dispatch(fetchUserProfile(fbUser.uid, { skipTags: location.pathname === '/tags' }))
          .then(result => {
            if (!result.success) {
              log('[SquarePlanApp.fetchUserProfile] Failed:', result.error);
              dispatchLogout();
              dispatch(setIsAppLoading(false));
              return;
            }

            const { user, account, billing, defaultBuilding } = result.data;
            log('[SquarePlanApp.fetchUserProfile] Profile loaded successfully');

            // Push notification setup
            if (typeof pushPlayerId !== 'undefined'
              && pushPlayerId !== null
              && pushPlayerId !== '') {
              SPBackend.setPushSubscriberId(fbUser, pushPlayerId || '')
                .catch(() => {});
            }

            // URL param handling for plan ID
            if (paramPlanId && paramPlanId !== '') {
              dispatch(setPlanId(paramPlanId));
              billing.planId = paramPlanId;
            }

            // Routing validation - only redirect if on billing-restricted page
            const currentPath = history.location.pathname;
            const needsBillingValidation = ['/member', '/billing', '/analytics', '/staff', '/customers', '/tags', '/integrations', '/share'].includes(currentPath);

            if (needsBillingValidation) {
              redirectBasedOnAccountStatus(history, {
                account,
                user,
                billing,
                defaultBuilding
              });
            }

            // Analytics tracking
            GA.set({ userId: user._id });
            Inspectlet.identify(user.Email);

            // Loading states
            dispatch(setLoadingModule({ account: false }));
            dispatch(setIsAppLoading(false));
          })
          .catch(err => {
            log('[SquarePlanApp.fetchUserProfile] err: ', err);
            dispatchLogout();
            dispatch(setIsAppLoading(false));
          });
      }
    });

    // Clean up the Firebase listener when component unmounts
    return () => unsubscribe();
  }, [pushPlayerId]);  // dispatch is stable per Redux docs - intentionally omitted

  // runs once per page load
  useEffect(() => {
    if (!i18nInitialized.current) {
      log('i18n init...', resources);
      i18n
        .init({
          resources,
          lng: 'en'
        })
        .then(() => {
          // Store i18n in Redux and window for legacy components
          dispatch(setI18n(i18n));
          window.i18n = i18n;
          i18nInitialized.current = true;
        });
    }

    // Register logout handler for automatic token refresh mechanism
    registerLogoutHandler(() => {
      log('[SquarePlanApp] Auto-logout triggered by token refresh failure');
      return dispatch(logout());
    });
  }, [dispatch]);
  
  return (
    <Router history={history}>
      <Switch>
        <Route
          exact
          path='/'
        ><LandingPage /></Route>
        {/* <Route component={() => { 
          window.location.href = 'https://dev--k8ajkf1.us.auth0.com/authorize'; 
          return null;
        }}
path='/authorize'
        /> */}
        <Route
          exact
          path='/authorize'
        ><AuthorizeHome /></Route>
        <Route
          exact
          path='/docs/api'
        ><ApiDocs /></Route>
        <Route
          exact
          path='/login'
        ><Login /></Route>
        <Route
          exact
          path='/login/:reset'
        ><Login /></Route>
        <Route
          exact
          path='/register'
        ><Signup /></Route>
        <Route
          exact
          path='/signup'
        ><SignupPage /></Route>
        <Route
          exact
          path='/plans'
        ><PlansHome /></Route>
        <Route
          exact
          path='/checkout'
        ><CheckoutHome /></Route>
        <PrivateRoute
          exact
          forceLogout={false}
          path='/subscribe'
        ><SubscribeHome /></PrivateRoute>
        <PrivateRoute
          exact
          forceLogout={false}
          path='/activate'
        ><ActivateHome /></PrivateRoute>
        <PrivateRoute
          exact
          forceLogout={false}
          path='/verifyPhone'
        ><VerifyPhoneHome /></PrivateRoute>
        <PrivateRoute
          exact
          forceLogout={false}
          path='/pickYourPhoneNumber'
        ><TwilioNumberHome /></PrivateRoute>
        <Route
          exact
          path='/staff/invitations/:inviteToken'
        ><StaffInvitationHome /></Route>
        <PrivateRoute
          exact
          forceLogout={false}
          path='/member'
        ><MemberHome /></PrivateRoute>
        <PrivateRoute
          exact
          forceLogout={false}
          path='/member/:requestId'
        ><RequestHome /></PrivateRoute>
        <PrivateRoute
          exact
          forceLogout={false}
          path='/neo'
        ><BotSettingsHome /></PrivateRoute>
        <PrivateRoute
          exact
          forceLogout={false}
          path='/analytics'
        ><AnalyticsHome /></PrivateRoute>
        {/* <PrivateRoute
          component={PropertiesHome}
          exact
          isLoggedIn={initialLoggedInState()}
          path='/properties'
        /> */}
        {/* <PrivateRoute
          component={PropertyHome}
          exact
          isLoggedIn={initialLoggedInState()}
          path='/property/:propertyKey'
        /> */}
        <PrivateRoute
          exact
          forceLogout={false}
          path='/staff'
        ><StaffListHome /></PrivateRoute>
        <PrivateRoute
          exact
          forceLogout={false}
          path='/customers'
        ><TenantsHome /></PrivateRoute>
        <PrivateRoute
          exact
          forceLogout={false}
          path='/billing'
        ><BillingHome /></PrivateRoute>
        <PrivateRoute
          exact
          forceLogout={false}
          path='/tags'
        ><TagsHome /></PrivateRoute>
        <PrivateRoute
          exact
          forceLogout={false}
          path='/integrations'
        ><IntegrationsHome /></PrivateRoute>
        <PrivateRoute
          exact
          forceLogout={false}
          path='/share'
        ><ReferralsHome /></PrivateRoute>
        <Route
          exact
          path='/logout'
        ><Logout /></Route>
        <Route><InvalidUrl /></Route>
      </Switch>
    </Router>
  );
};

export default SquarePlanApp;
